import {
    CombinedTimeSeriesOverview,
    StatDisplay,
} from 'components/TimeSeriesOverview';
import {
    formatSeriesData,
    getSeriesColors,
} from './Charts/MultiPredictionTimeSeriesChart/utils';
import { forwardRef, memo } from 'react';
import Chart from './Charts/MultiPredictionTimeSeriesChart';
import { INSTAGRAM_METADATA_BY_KEY } from 'utils/metrics';
import InstagramTimeSeriesNoDataMessage from 'components/DataVis/InstagramTimeSeriesNoDataMessage';
import TimeSeriesKey from './TimeSeriesKey';
import Tooltip from './Charts/MultiPredictionTimeSeriesChart/components/Tooltip';
import { timeseries as copy } from '@/copy';
import dayjs from 'dayjs';
import { getAverageTimeSeries } from './utils/index';
import { getXAxisTickFns } from './Charts/TikTokSearchTimeSeries/utils';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const InstagramTooltip = forwardRef(
    (
        {
            formatAndParseTime: formatAndParseTimeProp = formatAndParseTime,
            metricLabel = 'Instagram Likes',
            ...props
        },
        ref,
    ) => (
        <Tooltip
            {...props}
            formatAndParseTime={formatAndParseTimeProp}
            metricLabel={metricLabel}
            ref={ref}
        />
    ),
);

const InstagramTimeSeriesWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const defaultMetricsList = [
    INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_QoQ_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_MoM_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_WoW,
];

const InstagramTimeSeries = memo(
    forwardRef(
        (
            {
                data,
                week,
                year,
                isLoading,
                noDataMessage = <InstagramTimeSeriesNoDataMessage />,
                Wrapper = InstagramTimeSeriesWrapper,
                Tooltip = InstagramTooltip,
                metricsList = defaultMetricsList,
                metrics,
                defaultMetric = INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling,
                viewSumTimeSeries,
                chartHeight = 250,
                showStatDisplay = false,
                showDataSourceBar = true,
                customCss,
            },
            ref,
        ) => {
            const sortedData =
                data && data.length
                    ? data
                          .slice()
                          .sort(
                              (a, b) =>
                                  b.timeSeries.length - a.timeSeries.length,
                          )
                    : data;
            // need to have longest timeseries first for correctly establishing xAxis labels
            const { xAxisTickFormat, xAxisTickValues } =
                getXAxisTickFns(sortedData);
            let maybeSumData = data;
            if (viewSumTimeSeries && data && data.length) {
                const { accessors } = formatSeriesData(data);
                maybeSumData = [
                    {
                        id: 'combined',
                        timeSeries: getAverageTimeSeries(data, accessors),
                        color: getSeriesColors(0),
                        name: 'Combined',
                    },
                ];
            }
            return (
                <div css="margin-bottom: 12px;" ref={ref}>
                    <Chart
                        chartHeight={chartHeight}
                        data={maybeSumData}
                        isLoading={isLoading}
                        showPlaceholder={!data || data.length < 1}
                        placeholderMessage={
                            isLoading ? copy.loadingTikTok : noDataMessage
                        }
                        predictionStrokeDasharray={false}
                        Wrapper={Wrapper}
                        xAxisTickFormat={xAxisTickFormat}
                        xAxisTickValues={xAxisTickValues}
                        Tooltip={Tooltip}
                    >
                        <div
                            css={`
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                            `}
                        >
                            {showStatDisplay && (
                                <StatDisplay
                                    non_cum_avg_weekly_views={
                                        metrics.non_cum_avg_weekly_views
                                    }
                                    week={week}
                                    year={year}
                                    isLoading={isLoading}
                                    valueKey="non_cum_avg_weekly_likes"
                                    tooltipKey="non_cum_avg_weekly_likes"
                                    contentKey="dataTablePosts"
                                    labelPrefix="Avg. Weekly"
                                    labelSuffix="Likes"
                                />
                            )}
                            <CombinedTimeSeriesOverview
                                isLoading={isLoading}
                                metrics={
                                    metrics
                                        ? [
                                              {
                                                  metrics: metrics || {},
                                                  metricsList,
                                                  defaultMetric,
                                                  week,
                                                  year,
                                              },
                                          ]
                                        : null
                                }
                                showDataSource={false}
                                showDataSourceBar={showDataSourceBar}
                                customCss={customCss}
                                wrapperCss={'width: 74%;'}
                            />
                        </div>
                    </Chart>
                    {!viewSumTimeSeries ? (
                        <TimeSeriesKey
                            data={data}
                            style={{ display: 'none', marginTop: '1em' }}
                            className="image-download-visible"
                        />
                    ) : null}
                </div>
            );
        },
    ),
    isEqual,
);

export default InstagramTimeSeries;
